import React, { useState } from 'react';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RefreshCw, Volume2 } from 'lucide-react';
import { getScanFeedback, setScanFeedback, playScanFeedback } from '@/lib/scanFeedback';

const KEY = 'sc_auto_update';

// Per-device preference: whether the app should refresh itself quietly once a
// newer build is ready and the device is idle / backgrounded.
export const getAutoUpdate = () => {
  try { return localStorage.getItem(KEY) === '1'; } catch (e) { return false; }
};

// A small, universal (all-roles) device preference card for the Settings page.
export function AutoUpdatePanel() {
  const [on, setOn] = useState(getAutoUpdate());
  const toggle = (v) => {
    setOn(v);
    try { localStorage.setItem(KEY, v ? '1' : '0'); } catch (e) { /* ignore */ }
    // Let the live <UpdateBanner /> react immediately, without a reload.
    try { window.dispatchEvent(new CustomEvent('sc-auto-update-changed', { detail: v })); } catch (e) { /* ignore */ }
  };

  return (
    <div className="rounded-2xl border border-border bg-card p-4" data-testid="auto-update-panel">
      <div className="mb-1 flex items-center gap-2"><RefreshCw className="h-4 w-4" /><p className="text-sm font-semibold">App updates</p></div>
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-medium">Update quietly in the background</p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            When a new version is ready, this device refreshes on its own once it&rsquo;s idle or in the background &mdash;
            never mid-sale, and only when online with no sales waiting to sync. When off, you&rsquo;ll see a
            &ldquo;tap to refresh&rdquo; bar instead.
          </p>
        </div>
        <Switch checked={on} onCheckedChange={toggle} data-testid="auto-update-toggle" />
      </div>
      <div className="mt-4 border-t border-border pt-4">
        <div className="mb-1 flex items-center gap-2"><Volume2 className="h-4 w-4" /><p className="text-sm font-semibold">Scan sound</p></div>
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm font-medium">Feedback when a barcode is scanned</p>
            <p className="mt-0.5 text-xs text-muted-foreground">Choose how this device confirms a camera scan.</p>
          </div>
          <ScanSoundSelect />
        </div>
      </div>
    </div>
  );
}

function ScanSoundSelect() {
  const [val, setVal] = useState(getScanFeedback());
  const change = (v) => {
    setVal(v);
    setScanFeedback(v);
    if (v !== 'off') setTimeout(() => playScanFeedback(), 60); // preview the choice
  };
  return (
    <Select value={val} onValueChange={change}>
      <SelectTrigger data-testid="scan-sound-select" className="h-10 w-32 shrink-0 rounded-xl"><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value="beep">Beep</SelectItem>
        <SelectItem value="voice">Voice</SelectItem>
        <SelectItem value="off">Off</SelectItem>
      </SelectContent>
    </Select>
  );
}
